import React, { useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import { AppBar, Box, Container, IconButton, Toolbar } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import IconLogo from "../../assets/images/icons/Logo.svg";
import { IMenuItem } from "../../types/ui";
import { checkFeatureFlagEnabled } from "../../utils/env-flgs";
import ContactUsButton from "../ContactUsButton";
import HeaderMenu from "./HeaderMenu";
import MobileMenu from "./MobileMenu";
import {
  Root,
  LogoWrapper,
  NavList,
  mobileAppBar,
  MobileMenuContainer,
  mobileScrolled,
  scrolledComponent,
} from "./styles";

const HEADER_ITEMS: IMenuItem[] = [
  {
    id: "indexing",
    label: "Indexing",
    items: [
      {
        id: "index",
        label: "BITA Indexes",
        href: "/index",
      },
      {
        id: "index-services",
        label: "Index Services",
        href: "/index-services",
      },
      {
        id: "crypto",
        label: "Crypto Offering",
        href: "/products/crypto-offering",
        hidden: !checkFeatureFlagEnabled("CRYPTO_OFFERING_PAGE_ENABLED"),
      },
      {
        id: "indexlab",
        label: "Index Lab",
        href: "/products/index-lab",
        hidden: !checkFeatureFlagEnabled("INDEX_LAB_PAGE_ENABLED"),
      },
    ],
  },
  {
    id: "bitacore",
    label: "BITACore",
    href: "/products",
  },
  {
    id: "thematics",
    label: "Thematics",
    href: "/products/thematic-data",
  },
  {
    id: "esg-impact",
    label: "ESG & Impact",
    href: "/products/esg-impact-data",
  },
  {
    id: "portfolio-apis",
    label: "Indexing APIs",
    href: "/indexing-apis",
  },
  {
    id: "more",
    label: "More",
    items: [
      {
        id: "about",
        label: "About BITA",
        href: "/about",
        hidden: !checkFeatureFlagEnabled("ABOUT_US_PAGE_ENABLED"),
      },
      {
        id: "clients",
        label: "Our Clients",
        href: "/clients",
        hidden: !checkFeatureFlagEnabled("CLIENTS_PAGE_ENABLED"),
      },
      {
        id: "governance",
        label: "Governance",
        href: "/governance",
      },
      {
        id: "announcements",
        label: "Announcements",
        href: "/announcements",
      },
      //Temporily disabled until the blog is added to feat flag 
      // {
      //   id: "blog",
      //   label: "Blog",
      //   href: "https://bita.ghost.io",
      //   // hidden: !checkFeatureFlagEnabled("BLOG_PAGE_ENABLED"),// need to be added in env,gitlab-ci.yml, docker-compose.yml
      // },
    ],
  },
];

interface Props {
  color?: string;
  scrolled: boolean;
}

const Header: React.FC<Props> = ({ color = "#25246D", scrolled }) => {
  const location = useLocation();

  const [mobileMenuVisible, setMobileMenuVisible] = useState<boolean>(false);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuVisible(true);
  };

  const closeMenu = () => {
    setMobileMenuVisible(false);
  };

  const isInContactPage = location.pathname === "/contact-us";

  const MOBILE_ITEMS = [
    {
      id: "home",
      label: "Home",
      href: "/",
    },
    ...HEADER_ITEMS,
  ];

  return (
    <Box className="header">
      {/* mobile menu */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <AppBar
          position="fixed"
          sx={{
            ...(scrolled ? { ...mobileAppBar, ...mobileScrolled } : ""),
            ...mobileAppBar,
          }}
        >
          <Toolbar>
            <MobileMenuContainer>
              <Box>
                <Link to="/">
                  <img src={IconLogo} alt="BITA Logo" />
                </Link>
              </Box>
              <IconButton
                size="large"
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={openMenu}
              >
                <MenuIcon />
              </IconButton>
              <MobileMenu
                open={mobileMenuVisible}
                items={MOBILE_ITEMS}
                onClose={closeMenu}
              />
            </MobileMenuContainer>
          </Toolbar>
        </AppBar>
      </Box>

      {/* desktop menu */}
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <Root
          sx={{
            ...(scrolled ? { ...scrolledComponent } : ""),
          }}
        >
          <LogoWrapper>
            <Link to="/">
              <img src={IconLogo} alt="BITA Logo" />
            </Link>
          </LogoWrapper>
          <Container
            sx={{
              flex: 1,
              "&.MuiContainer-root": {
                marginLeft: 0,
                marginRight: 0,
              },
            }}
          >
            <NavList>
              {HEADER_ITEMS.map(({ id, label, href, items, hidden }) =>
                hidden ? null : (
                  <Box
                    key={label}
                    sx={{
                      paddingLeft: "1rem",
                      paddingRight: "1rem",
                      color: "#fff",
                      fontSize: "1rem",
                      lineHeight: "18px",
                    }}
                  >
                    {items?.length ? (
                      <HeaderMenu key={id} label={label} items={items} />
                    ) : (
                      <Link to={href ?? "#"}>{label}</Link>
                    )}
                  </Box>
                )
              )}
            </NavList>
          </Container>

          {isInContactPage ? null : <ContactUsButton color={color} />}
        </Root>
      </Box>
    </Box>
  );
};

export default Header;
